import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, CaretLeft, CaretRight, MagnifyingGlass, Plus, Users, Receipt } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';

const PAGE_SIZE = 25;

export default function OrdersPage() {
  const navigate = useNavigate();
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  // Search state
  const [searchForm, setSearchForm] = useState({ orderId: '', productId: '', productName: '', orderState: '', initDate: '', endDate: '', clientId: '', clientName: '' });
  const [states, setStates] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  const [searching, setSearching] = useState(false);

  // Orders list state
  const [orders, setOrders] = useState([]);
  const [billing, setBilling] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchOrders(1);
    api.post('/encomendas/states').then(r => setStates(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const fetchOrders = (p) => {
    setLoading(true);
    api.post('/encomendas/all', { page: p, page_size: PAGE_SIZE })
      .then(r => {
        const list = r.data?.data || [];
        setOrders(list);
        setTotalPages(r.data?.total_pages || 1);
        setTotal(r.data?.total || 0);
        setPage(r.data?.page || p);
        setLoading(false);
        // Carrega documentos em batch para esta página
        const ids = list.map(o => o.ID_Encomenda).filter(Boolean);
        if (ids.length > 0) {
          api.post('/encomendas/billing-batch', { order_ids: ids })
            .then(rb => { if (rb.data?.success) setBilling(rb.data.billing || {}); })
            .catch(() => {});
        }
      })
      .catch(() => setLoading(false));
  };

  const handleSearch = (p = 1) => {
    setSearching(true);
    api.post('/encomendas/search', { ...searchForm, page: p, pageSize: PAGE_SIZE })
      .then(r => {
        setSearchResults(r.data?.data || []);
        setSearchTotal(r.data?.total || 0);
        setSearchTotalPages(r.data?.totalPages || 0);
        setSearchPage(r.data?.page || p);
        setSearching(false);
      })
      .catch(() => { setSearchResults([]); setSearching(false); });
  };

  const clearSearch = () => {
    setSearchForm({ productId: '', productName: '', orderState: '', initDate: '', endDate: '', clientId: '', clientName: '' });
    setSearchResults(null);
  };

  const sf = (field, val) => setSearchForm(prev => ({ ...prev, [field]: val }));

  return (
    <div data-testid="orders-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Encomendas</h1>
          <p className="text-sm text-[#6B7280] mt-1">Página onde pode ver as encomendas da sua rede</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/encomendas/rede-pendentes')} data-testid="downline-pending-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E7EB] bg-white text-[#0F1829] text-sm font-medium rounded-lg hover:bg-[#F9FAFB] transition-colors">
            <Users size={16} /> Pendentes da Rede
          </button>
          <button onClick={() => navigate('/encomendas/nova')} data-testid="new-order-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-lg hover:bg-[#001f6e] transition-colors">
            <Plus size={16} /> Nova Encomenda
          </button>
        </div>
      </div>

      {/* PESQUISA */}
      <div data-testid="order-search" className="bg-white border border-[#E5E7EB] rounded-lg mb-4 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] flex items-center gap-2">
          <MagnifyingGlass size={16} className="text-[#6B7280]" />
          <h2 className="text-sm font-semibold text-[#111827]">Pesquisar Produtos Comprados</h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">ID da Encomenda</label>
              <input data-testid="search-order-id" type="number" value={searchForm.orderId} onChange={e => sf('orderId', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none font-mono" placeholder="Ex: 70392" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">ID do Produto</label>
              <input data-testid="search-product-id" type="number" value={searchForm.productId} onChange={e => sf('productId', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="ID..." />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Nome do Produto</label>
              <input data-testid="search-product-name" value={searchForm.productName} onChange={e => sf('productName', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Nome produto..." />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">ID do Cliente</label>
              <input data-testid="search-client-id" type="number" value={searchForm.clientId} onChange={e => sf('clientId', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="ID..." />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Nome do Cliente</label>
              <input data-testid="search-client-name" value={searchForm.clientName} onChange={e => sf('clientName', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" placeholder="Nome cliente..." />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Estado da Encomenda</label>
              <select data-testid="search-state" value={searchForm.orderState} onChange={e => sf('orderState', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none">
                <option value="">Todos</option>
                {states.map(s => <option key={s.ID} value={s.ID}>{s.Descricao}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Data Início</label>
              <input data-testid="search-date-from" type="date" value={searchForm.initDate} onChange={e => sf('initDate', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block">Data Fim</label>
              <input data-testid="search-date-to" type="date" value={searchForm.endDate} onChange={e => sf('endDate', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#0F1829] outline-none" />
            </div>
            <div className="flex items-end gap-2">
              <button data-testid="search-btn" onClick={() => handleSearch(1)} disabled={searching}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded hover:bg-[#001f7a] transition-colors disabled:opacity-50 flex-1 justify-center">
                {searching ? <SpinnerGap size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />} Pesquisar
              </button>
              {searchResults !== null && (
                <button data-testid="clear-search-btn" onClick={clearSearch}
                  className="px-3 py-2 text-sm font-medium border border-[#E5E7EB] text-[#374151] rounded hover:bg-[#F9FAFB]">Limpar</button>
              )}
            </div>
          </div>

          {/* Search Results */}
          {searchResults !== null && (
            <div data-testid="search-results" className="border-t border-[#E5E7EB] pt-4">
              <p className="text-xs text-[#6B7280] mb-3">N.º de resultados: <span className="font-semibold">{searchTotal}</span></p>
              {searchResults.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                      <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">ID Enc.</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Cliente</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Produto</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Data</th>
                      <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Estado</th>
                      <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Qtd</th>
                    </tr></thead>
                    <tbody>
                      {searchResults.map((r, i) => (
                        <tr key={`s-${r.ID_Encomenda || 'e'}-${r.ID_Produto || r.Produto || i}-${i}`} data-testid={`search-row-${i}`}
                          onClick={() => navigate(`/encomendas/${r.ID_Encomenda}`)}
                          className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors">
                          <td className="px-3 py-2.5 text-sm font-mono text-[#0F1829] underline">{r.ID_Encomenda}</td>
                          <td className="px-3 py-2.5 text-sm text-[#111827]">{r.ID_Cliente} - <PersonLink id={r.ID_Cliente} name={r.NomeCliente} /></td>
                          <td className="px-3 py-2.5 text-sm text-[#6B7280]">{r.Produto || '-'}</td>
                          <td className="px-3 py-2.5 text-sm text-[#6B7280]">{r.DataEncomenda || '-'}</td>
                          <td className="px-3 py-2.5"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{r.EstadoEncomenda}</span></td>
                          <td className="px-3 py-2.5 text-sm font-mono text-center">{r.Quantidade}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {searchTotalPages > 1 && (
                    <Pagination page={searchPage} totalPages={searchTotalPages} total={searchTotal} onPageChange={(p) => handleSearch(p)} />
                  )}
                </>
              ) : <p className="text-sm text-[#6B7280] text-center py-4">Sem resultados para a pesquisa</p>}
            </div>
          )}
        </div>
      </div>

      {/* LISTA DE ENCOMENDAS */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h2 className="text-sm font-semibold text-[#111827]">Encomendas ({total})</h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><SpinnerGap size={24} className="animate-spin text-[#0F1829]" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">ID</th>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Cliente</th>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Estado</th>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Data Encomenda</th>
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Data Envio</th>
                <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Docs</th>
                <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-3">Valor</th>
              </tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.ID_Encomenda}
                    data-testid={`order-row-${o.ID_Encomenda}`}
                    onClick={() => navigate(`/encomendas/${o.ID_Encomenda}`)}
                    className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors">
                    <td className="px-4 py-3 text-sm font-mono font-medium text-[#0F1829] underline">{o.ID_Encomenda}</td>
                    <td className="px-4 py-3 text-sm">{o.ID_Cliente} - <PersonLink id={o.ID_Cliente} name={o.NomeCliente} /></td>
                    <td className="px-4 py-3"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{o.EstadoEncomenda}</span></td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{o.DataEncomenda || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]">{o.DataEnvio || '-'}</td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <DocLinks docs={billing[o.ID_Encomenda]} orderId={o.ID_Encomenda} />
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(o.PrecoFinal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {orders.length === 0 && <p className="text-center py-6 text-sm text-[#6B7280]">Sem encomendas registadas</p>}
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} total={total} onPageChange={fetchOrders} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DocLinks({ docs, orderId }) {
  if (!docs) return <span className="text-xs text-[#D1D5DB]">—</span>;
  const items = [];
  (docs.facturas || []).forEach((f, i) => {
    if (f.UrlFatura) items.push({ key: `ft-${orderId}-${i}`, url: f.UrlFatura, label: (f.TipoFactura === 'F' ? 'F' : (f.TipoFactura === 'FR' ? 'FR' : 'F')), nr: f.NrFactura, cls: 'bg-blue-50 text-blue-900 border-blue-200', title: `Fatura ${f.NrFactura}` });
  });
  (docs.recibos || []).forEach((r, i) => {
    if (r.UrlRecibo) items.push({ key: `rb-${orderId}-${i}`, url: r.UrlRecibo, label: 'R', nr: r.NrRecibo, cls: 'bg-emerald-50 text-emerald-900 border-emerald-200', title: `Recibo ${r.NrRecibo}` });
  });
  (docs.notas_credito || []).forEach((n, i) => {
    if (n.UrlNotaCredito) items.push({ key: `nc-${orderId}-${i}`, url: n.UrlNotaCredito, label: 'NC', nr: n.NrNotaCredito, cls: 'bg-amber-50 text-amber-900 border-amber-200', title: `Nota de Crédito ${n.NrNotaCredito}` });
  });
  if (items.length === 0) return <span className="text-xs text-[#D1D5DB]">—</span>;
  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      {items.map((it) => (
        <a
          key={it.key}
          href={it.url}
          target="_blank"
          rel="noopener noreferrer"
          title={it.title}
          data-testid={`doc-link-${it.key}`}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded border ${it.cls} hover:opacity-80`}
        >
          <Receipt size={11} />{it.label}
        </a>
      ))}
    </div>
  );
}

function Pagination({ page, totalPages, total, onPageChange }) {
  return (
    <div data-testid="pagination" className="flex flex-col items-center gap-2 px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
      <p className="text-xs text-[#6B7280]">Página {page} de {totalPages} ({total} registos)</p>
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onPageChange(page - 1)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          <CaretLeft size={12} /> Anterior
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let p;
          if (totalPages <= 5) p = i + 1;
          else if (page <= 3) p = i + 1;
          else if (page >= totalPages - 2) p = totalPages - 4 + i;
          else p = page - 2 + i;
          return (
            <button key={p} onClick={() => onPageChange(p)}
              className={`w-8 h-8 text-xs font-medium rounded border transition-colors ${p === page ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-white'}`}>
              {p}
            </button>
          );
        })}
        <button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          Seguinte <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
}
